import { useState } from "react";
import type { ReactNode } from "react";
import { cx } from "@/lib/format";

/**
 * A chip.
 *
 * 22px, 11px at weight 600, on `--surface-2` behind a hairline, 6px corner —
 * never a pill. Every identifier in the product wears one: a ULID, an image
 * digest, a hostname, an invoice number, a region slug. That is what makes
 * them recognisable as the same *kind* of thing without reading them.
 *
 * It matters more here than it would elsewhere because this product's objects
 * are named by 26-character identifiers that nobody reads and everybody
 * copies. A chip says "this is a handle, not a sentence".
 */
/**
 * The four things a chip can say.
 *
 * Deliberately not a "neutral" member: a chip with no tone *is* the neutral
 * one, and a named neutral invites `tone="neutral"` to be written out in
 * forty places where writing nothing says the same thing. A helper that
 * computes a tone returns `ChipTone | undefined` for the same reason.
 */
export type ChipTone = "up" | "warn" | "down" | "live";

export function Chip({
  children,
  tone,
  className,
}: {
  children: ReactNode;
  /** Only for a chip that carries a state. Most chips carry an identifier. */
  tone?: ChipTone;
  className?: string;
}) {
  const tones = {
    up: "border-[color-mix(in_oklab,var(--success)_35%,transparent)] text-[var(--success)]",
    warn: "border-[color-mix(in_oklab,var(--warning)_35%,transparent)] text-[var(--warning)]",
    down: "border-[color-mix(in_oklab,var(--danger)_35%,transparent)] text-[var(--danger)]",
    live: "border-[color-mix(in_oklab,var(--primary)_35%,transparent)] text-[var(--primary)]",
  };
  return <span className={cx("chip", tone && tones[tone], className)}>{children}</span>;
}

/**
 * An identifier you can take with you.
 *
 * The copy control is part of the chip rather than a button beside it,
 * because in this product the identifier and the act of copying it are the
 * same gesture — nobody reads a ULID for its own sake.
 *
 * Confirmation is the chip itself going live for a second. A toast for
 * "copied" is a notification about something the person just watched happen.
 */
export function Handle({
  value,
  label,
  truncate,
  className,
}: {
  value: string;
  /** What this identifies, for the screen reader. "Service ID", "Digest". */
  label: string;
  /** Show only the first n characters. Digests are 71 characters long. */
  truncate?: number;
  className?: string;
}) {
  const [taken, setTaken] = useState(false);
  const shown = truncate && value.length > truncate ? value.slice(0, truncate) : value;

  return (
    <button
      type="button"
      aria-label={`Copy ${label}: ${value}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(
          () => {
            setTaken(true);
            window.setTimeout(() => setTaken(false), 1200);
          },
          () => undefined,
        );
      }}
      className={cx(
        "chip press cursor-pointer normal-case",
        taken
          ? "border-[color-mix(in_oklab,var(--primary)_45%,transparent)] text-[var(--primary)]"
          : "hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]",
        className,
      )}
    >
      <span className="truncate">{taken ? "Copied" : shown}</span>
      {!taken && truncate && value.length > truncate && (
        <span className="text-[var(--text-faint)]">…</span>
      )}
    </button>
  );
}
